import { useEffect, useState } from 'react'
import { newBackendClient } from '../clients/new-backend.client'
import { Query } from '../../generated/nest-graphql'
import { GET_GBL_LOCATIONS } from '../../lib/locations/queries'

export type GblLocationRow = NonNullable<Query['getGblLocations']>[number]

/** Group active GBL locations by full state name (for section headings). */
export function buildStateLocationMap(
  locations: GblLocationRow[]
): Record<string, GblLocationRow[]> {
  const active = locations.filter((l) => l.isActive)
  const grouped = Object.values(
    active.reduce(
      (acc, location) => {
        if (!acc[location.state]) {
          acc[location.state] = {
            state: location.stateName,
            locations: [] as GblLocationRow[],
          }
        }
        acc[location.state].locations.push(location)
        return acc
      },
      {} as Record<string, { state: string; locations: GblLocationRow[] }>
    )
  )
  const unsorted = grouped.reduce(
    (acc, group) => {
      acc[group.state] = group.locations
      return acc
    },
    {} as Record<string, GblLocationRow[]>
  )
  const sortedEntries = Object.entries(unsorted).sort(
    (a, b) => b[1].length - a[1].length
  )
  return Object.fromEntries(sortedEntries) as Record<string, GblLocationRow[]>
}

/**
 * Loads consultation locations from GraphQL and groups them by state name.
 * Safe to use in any client component (no Apollo Provider required).
 */
export function useGblLocationsByState(logLabel = 'GblLocations') {
  const [stateLocationMap, setStateLocationMap] = useState<
    Record<string, GblLocationRow[]>
  >({})

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await newBackendClient.query<Query>({
          query: GET_GBL_LOCATIONS,
          fetchPolicy: 'network-only',
        })
        if (cancelled) return
        const list = data?.getGblLocations ?? []
        setStateLocationMap(buildStateLocationMap(list))
      } catch (e) {
        console.error(`[${logLabel}] Failed to load locations from API`, e)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  return stateLocationMap
}
